// Comprehensive UK Pharmacy vaccine database with detailed information

export interface VaccineOption {
  id: string;
  disease: string;
  brandName: string;
  manufacturer: string;
  displayText: string;
  searchTerms: string[];
  // Detailed information
  description?: string;
  dosage?: string;
  schedule?: string;
  route?: string;
  contraindications?: string[];
  sideEffects?: string[];
  storageConditions?: string;
  ageRange?: string;
  boosterRequired?: boolean;
  boosterSchedule?: string;
  nhsEligibility?: string;
  privatePrice?: string;
}

export const vaccineDatabase: VaccineOption[] = [
  // Hepatitis A
  {
    id: "696d189477123322531e343a",
    disease: "Hepatitis A",
    brandName: "Avaxim",
    manufacturer: "Sanofi Pasteur",
    displayText: "Hepatitis A - Avaxim (Sanofi Pasteur)",
    searchTerms: ["hepatitis a", "avaxim", "sanofi", "hep a"],
    description: "Inactivated hepatitis A vaccine for active immunisation against hepatitis A virus infection.",
    dosage: "0.5ml single dose",
    schedule: "Single dose with booster at 6-12 months",
    route: "Intramuscular (deltoid)",
    ageRange: "16 years and above",
    contraindications: ["Hypersensitivity to vaccine components", "Acute severe febrile illness"],
    sideEffects: ["Injection site pain", "Headache", "Fatigue", "Myalgia"],
    storageConditions: "2-8°C, do not freeze",
    boosterRequired: true,
    boosterSchedule: "6-12 months after first dose",
    nhsEligibility: "Travelers to endemic areas, occupational risk",
    privatePrice: "£45-55",
  },
  {
    id: "696d189477123322531e343b",
    disease: "Hepatitis A",
    brandName: "Havrix",
    manufacturer: "GSK",
    displayText: "Hepatitis A - Havrix (GSK)",
    searchTerms: ["hepatitis a", "havrix", "gsk", "hep a"],
    description: "Inactivated hepatitis A vaccine providing protection against hepatitis A infection.",
    dosage: "1ml (1440 ELISA units)",
    schedule: "Single dose with booster at 6-12 months",
    route: "Intramuscular",
    ageRange: "16 years and above",
    contraindications: ["Hypersensitivity", "Postpone if acute illness"],
    sideEffects: ["Local reactions", "Headache", "Malaise"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "6-12 months",
    privatePrice: "£50-60",
  },
  {
    id: "696d189477123322531e343c",
    disease: "Hepatitis A",
    brandName: "Vaqta",
    manufacturer: "Merck",
    displayText: "Hepatitis A - Vaqta (Merck)",
    searchTerms: ["hepatitis a", "vaqta", "merck", "hep a"],
    description: "Hepatitis A vaccine for active immunization.",
    dosage: "1ml (50 units)",
    schedule: "2-dose series",
    route: "Intramuscular",
    ageRange: "18 years and above",
    boosterRequired: true,
    boosterSchedule: "6-18 months",
    privatePrice: "£45-55",
  },
  
  // Hepatitis B
  {
    id: "696d189477123322531e343d",
    disease: "Hepatitis B",
    brandName: "Engerix-B",
    manufacturer: "GSK",
    displayText: "Hepatitis B - Engerix-B (GSK)",
    searchTerms: ["hepatitis b", "engerix", "engerix-b", "gsk", "hep b"],
    description: "Recombinant hepatitis B vaccine for active immunisation.",
    dosage: "1ml (20 micrograms)",
    schedule: "3-dose series at 0, 1, 6 months",
    route: "Intramuscular (deltoid)",
    ageRange: "16 years and above",
    contraindications: ["Hypersensitivity to yeast or vaccine components"],
    sideEffects: ["Injection site reactions", "Fatigue", "Fever"],
    storageConditions: "2-8°C, do not freeze",
    boosterRequired: false,
    nhsEligibility: "Healthcare workers, at-risk groups",
    privatePrice: "£30-40 per dose",
  },
  {
    id: "696d189477123322531e343e",
    disease: "Hepatitis B",
    brandName: "HBvaxPRO",
    manufacturer: "Merck",
    displayText: "Hepatitis B - HBvaxPRO (Merck)",
    searchTerms: ["hepatitis b", "hbvaxpro", "merck", "hep b"],
    description: "Recombinant hepatitis B vaccine.",
    dosage: "1ml (10 micrograms)",
    schedule: "0, 1, 6 months",
    route: "Intramuscular",
    ageRange: "16 years and above",
    boosterRequired: false,
    privatePrice: "£30-40",
  },
  
  // Hepatitis A & B Combined
  {
    id: "696d189477123322531e343f",
    disease: "Hepatitis A & B",
    brandName: "Twinrix",
    manufacturer: "GSK",
    displayText: "Hepatitis A & B - Twinrix (GSK)",
    searchTerms: ["hepatitis a", "hepatitis b", "twinrix", "gsk", "hep a", "hep b", "hepatitis a and b", "combined"],
    description: "Combined hepatitis A and B vaccine for simultaneous protection.",
    dosage: "1ml",
    schedule: "3 doses at 0, 1, 6 months (or accelerated 0, 7, 21 days + booster at 12 months)",
    route: "Intramuscular",
    ageRange: "16 years and above",
    contraindications: ["Hypersensitivity to vaccine components"],
    sideEffects: ["Injection site pain", "Headache", "Fatigue"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "After 20 years or as recommended",
    nhsEligibility: "Healthcare workers, travelers",
    privatePrice: "£65-75 per dose",
  },
  
  // Typhoid
  {
    id: "696d189477123322531e343i",
    disease: "Typhoid",
    brandName: "Typhim Vi",
    manufacturer: "Sanofi Pasteur",
    displayText: "Typhoid - Typhim Vi (Sanofi Pasteur)",
    searchTerms: ["typhoid", "typhim", "sanofi"],
    description: "Polysaccharide typhoid vaccine for protection against Salmonella typhi.",
    dosage: "0.5ml",
    schedule: "Single dose",
    route: "Intramuscular or subcutaneous",
    ageRange: "2 years and above",
    contraindications: ["Hypersensitivity", "Acute infection"],
    sideEffects: ["Injection site reactions", "Fever", "Headache"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "Every 3 years if ongoing risk",
    nhsEligibility: "Travelers to endemic areas",
    privatePrice: "£35-45",
  },
  {
    id: "696d189477123322531e343g",
    disease: "Typhoid",
    brandName: "Vivotif",
    manufacturer: "PaxVax",
    displayText: "Typhoid - Vivotif Oral (PaxVax)",
    searchTerms: ["typhoid", "vivotif", "paxvax", "oral"],
    description: "Live attenuated oral typhoid vaccine.",
    dosage: "3 capsules",
    schedule: "1 capsule on alternate days (days 1, 3, 5)",
    route: "Oral",
    ageRange: "5 years and above",
    contraindications: ["Immunosuppression", "Pregnancy", "Acute GI illness"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "Every 3 years",
    privatePrice: "£40-50",
  },
  
  // Yellow Fever
  {
    id: "696d189477123322531e343h",
    disease: "Yellow Fever",
    brandName: "Stamaril",
    manufacturer: "Sanofi Pasteur",
    displayText: "Yellow Fever - Stamaril (Sanofi Pasteur)",
    searchTerms: ["yellow fever", "stamaril", "sanofi"],
    description: "Live attenuated yellow fever vaccine. Certificate valid for life.",
    dosage: "0.5ml",
    schedule: "Single dose",
    route: "Subcutaneous",
    ageRange: "9 months and above",
    contraindications: ["Age <6 months", "Immunosuppression", "Egg allergy", "Thymus disorder"],
    sideEffects: ["Headache", "Myalgia", "Low-grade fever"],
    storageConditions: "2-8°C, use within 6 hours once reconstituted",
    boosterRequired: false,
    nhsEligibility: "Travel to endemic areas (certificate required)",
    privatePrice: "£65-75",
  },
  
  // Meningitis ACWY
  {
    id: "696d189477123322531e344i",
    disease: "Meningitis ACWY",
    brandName: "Menveo",
    manufacturer: "GSK",
    displayText: "Meningitis ACWY - Menveo (GSK)",
    searchTerms: ["meningitis", "menveo", "gsk", "acwy", "meningococcal"],
    description: "Conjugate vaccine against meningococcal groups A, C, W135, and Y.",
    dosage: "0.5ml",
    schedule: "Single dose",
    route: "Intramuscular",
    ageRange: "2 years and above",
    contraindications: ["Hypersensitivity"],
    sideEffects: ["Injection site pain", "Headache", "Malaise"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "Every 3-5 years if ongoing risk",
    nhsEligibility: "Hajj pilgrims, students, at-risk groups",
    privatePrice: "£50-60",
  },
  {
    id: "696d189477123322531e344j",
    disease: "Meningitis ACWY",
    brandName: "Nimenrix",
    manufacturer: "Pfizer",
    displayText: "Meningitis ACWY - Nimenrix (Pfizer)",
    searchTerms: ["meningitis", "nimenrix", "pfizer", "acwy", "meningococcal"],
    description: "Meningococcal ACWY conjugate vaccine.",
    dosage: "0.5ml",
    schedule: "Single dose",
    route: "Intramuscular",
    ageRange: "12 months and above",
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "Every 5 years",
    privatePrice: "£50-60",
  },
  {
    id: "696d189477123322531e344k",
    disease: "Meningitis B",
    brandName: "Bexsero",
    manufacturer: "GSK",
    displayText: "Meningitis B - Bexsero (GSK)",
    searchTerms: ["meningitis b", "bexsero", "gsk", "meningococcal b"],
    description: "Meningococcal group B vaccine.",
    dosage: "0.5ml",
    schedule: "2 doses, 1 month apart",
    route: "Intramuscular",
    ageRange: "2 months and above",
    contraindications: ["Hypersensitivity"],
    sideEffects: ["Fever (common in infants)", "Injection site reactions"],
    storageConditions: "2-8°C",
    nhsEligibility: "Infants (routine schedule), at-risk groups",
    privatePrice: "£95-110 per dose",
  },
  
  // Rabies
  {
    id: "696d189477123322531e344l",
    disease: "Rabies",
    brandName: "Rabipur",
    manufacturer: "GSK",
    displayText: "Rabies - Rabipur (GSK)",
    searchTerms: ["rabies", "rabipur", "gsk"],
    description: "Inactivated rabies vaccine for pre and post-exposure prophylaxis.",
    dosage: "1ml",
    schedule: "Pre-exposure: 3 doses at days 0, 7, 21/28. Post-exposure: varies",
    route: "Intramuscular (deltoid)",
    ageRange: "All ages",
    contraindications: ["None for post-exposure", "Hypersensitivity for pre-exposure"],
    sideEffects: ["Injection site reactions", "Headache", "Myalgia"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "Every 2-5 years for ongoing risk",
    nhsEligibility: "Post-exposure (free), pre-exposure (private)",
    privatePrice: "£55-65 per dose",
  },
  {
    id: "696d189477123322531e344m",
    disease: "Rabies",
    brandName: "Verorab",
    manufacturer: "Sanofi Pasteur",
    displayText: "Rabies - Verorab (Sanofi Pasteur)",
    searchTerms: ["rabies", "verorab", "sanofi"],
    description: "Purified vero cell rabies vaccine.",
    dosage: "0.5ml",
    schedule: "3-dose pre-exposure or post-exposure series",
    route: "Intramuscular",
    ageRange: "All ages",
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "Every 2-5 years",
    privatePrice: "£55-65",
  },
  
  // Japanese Encephalitis
  {
    id: "696d189477123322531e344n",
    disease: "Japanese Encephalitis",
    brandName: "Ixiaro",
    manufacturer: "Valneva",
    displayText: "Japanese Encephalitis - Ixiaro (Valneva)",
    searchTerms: ["japanese encephalitis", "ixiaro", "valneva", "je"],
    description: "Inactivated vaccine against Japanese encephalitis virus.",
    dosage: "0.5ml",
    schedule: "2 doses at days 0 and 28",
    route: "Intramuscular",
    ageRange: "2 months and above",
    contraindications: ["Hypersensitivity"],
    sideEffects: ["Headache", "Myalgia", "Injection site pain"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "12-24 months after primary course",
    nhsEligibility: "Travelers to endemic areas (private)",
    privatePrice: "£90-100 per dose",
  },
  
  // Tick-Borne Encephalitis
  {
    id: "696d189477123322531e344o",
    disease: "Tick-Borne Encephalitis",
    brandName: "TicoVac",
    manufacturer: "Pfizer",
    displayText: "Tick-Borne Encephalitis - TicoVac (Pfizer)",
    searchTerms: ["tick-borne encephalitis", "ticovac", "pfizer", "tbe", "tick encephalitis"],
    description: "Inactivated vaccine against tick-borne encephalitis.",
    dosage: "0.5ml",
    schedule: "3 doses at 0, 1-3 months, 5-12 months",
    route: "Intramuscular",
    ageRange: "16 years and above",
    contraindications: ["Egg allergy", "Acute febrile illness"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "Every 3 years",
    privatePrice: "£65-75 per dose",
  },
  
  // Cholera
  {
    id: "696d189477123322531e344p",
    disease: "Cholera",
    brandName: "Dukoral",
    manufacturer: "Valneva",
    displayText: "Cholera - Dukoral (Valneva)",
    searchTerms: ["cholera", "dukoral", "valneva", "travellers diarrhoea"],
    description: "Oral cholera vaccine, also provides limited protection against ETEC diarrhoea.",
    dosage: "Oral suspension",
    schedule: "2 doses, 1-6 weeks apart (3 doses for children 2-6 years)",
    route: "Oral",
    ageRange: "2 years and above",
    contraindications: ["Acute GI illness", "Immunosuppression"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "After 2 years",
    nhsEligibility: "Private only",
    privatePrice: "£35-45 per dose",
  },
  
  // COVID-19
  {
    id: "696d189477123322531e344q",
    disease: "COVID-19",
    brandName: "Comirnaty",
    manufacturer: "Pfizer-BioNTech",
    displayText: "COVID-19 - Comirnaty (Pfizer-BioNTech)",
    searchTerms: ["covid", "covid-19", "coronavirus", "comirnaty", "pfizer", "biontech"],
    description: "mRNA vaccine against COVID-19.",
    dosage: "0.3ml",
    schedule: "Primary course + boosters as per current guidelines",
    route: "Intramuscular",
    ageRange: "6 months and above",
    contraindications: ["Hypersensitivity to PEG"],
    sideEffects: ["Injection site pain", "Fatigue", "Headache", "Myalgia"],
    storageConditions: "Ultra-cold or 2-8°C depending on formulation",
    boosterRequired: true,
    boosterSchedule: "As per national guidance",
    nhsEligibility: "NHS programme",
    privatePrice: "£30-40 (if privately available)",
  },
  {
    id: "696d189477123322531e344r",
    disease: "COVID-19",
    brandName: "Spikevax",
    manufacturer: "Moderna",
    displayText: "COVID-19 - Spikevax (Moderna)",
    searchTerms: ["covid", "covid-19", "coronavirus", "spikevax", "moderna"],
    description: "mRNA COVID-19 vaccine.",
    dosage: "0.5ml or 0.25ml (depending on age/booster)",
    schedule: "Per current guidelines",
    route: "Intramuscular",
    ageRange: "6 months and above",
    storageConditions: "Freezer or 2-8°C",
    nhsEligibility: "NHS programme",
  },
  
  // Influenza
  {
    id: "696d189477123322531e344s",
    disease: "Influenza",
    brandName: "Quadrivalent Flu Vaccine",
    manufacturer: "Various",
    displayText: "Influenza - Quadrivalent (Various)",
    searchTerms: ["influenza", "flu", "quadrivalent"],
    description: "Inactivated influenza vaccine protecting against 4 strains.",
    dosage: "0.5ml",
    schedule: "Annual (September-March)",
    route: "Intramuscular",
    ageRange: "6 months and above",
    contraindications: ["Egg allergy (for egg-based vaccines)", "Previous severe reaction"],
    sideEffects: ["Injection site soreness", "Mild fever", "Muscle aches"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "Annually",
    nhsEligibility: "65+, at-risk groups, healthcare workers, pregnant women",
    privatePrice: "£15-25",
  },
  
  // Pneumococcal
  {
    id: "696d189477123322531e344t",
    disease: "Pneumococcal",
    brandName: "Prevenar 13",
    manufacturer: "Pfizer",
    displayText: "Pneumococcal - Prevenar 13 (Pfizer)",
    searchTerms: ["pneumococcal", "prevenar", "pfizer", "pneumonia"],
    description: "13-valent pneumococcal conjugate vaccine.",
    dosage: "0.5ml",
    schedule: "Single dose (adults), infant schedule varies",
    route: "Intramuscular",
    ageRange: "6 weeks and above",
    contraindications: ["Hypersensitivity"],
    storageConditions: "2-8°C",
    nhsEligibility: "65+, at-risk groups, infants",
    privatePrice: "£75-85",
  },
  {
    id: "696d189477123322531e344u",
    disease: "Pneumococcal",
    brandName: "Pneumovax 23",
    manufacturer: "Merck",
    displayText: "Pneumococcal - Pneumovax 23 (Merck)",
    searchTerms: ["pneumococcal", "pneumovax", "merck", "pneumonia"],
    description: "23-valent pneumococcal polysaccharide vaccine.",
    dosage: "0.5ml",
    schedule: "Single dose",
    route: "Intramuscular or subcutaneous",
    ageRange: "2 years and above",
    boosterRequired: true,
    boosterSchedule: "After 5 years for certain at-risk groups",
    nhsEligibility: "65+, at-risk groups",
    privatePrice: "£45-55",
  },
  
  // MMR
  {
    id: "696d189477123322531e344v",
    disease: "MMR (Measles, Mumps, Rubella)",
    brandName: "Priorix",
    manufacturer: "GSK",
    displayText: "MMR - Priorix (GSK)",
    searchTerms: ["mmr", "measles", "mumps", "rubella", "priorix", "gsk"],
    description: "Live attenuated measles, mumps and rubella vaccine.",
    dosage: "0.5ml",
    schedule: "2 doses (infant schedule: 12-13 months and 3-4 years)",
    route: "Subcutaneous or intramuscular",
    ageRange: "9 months and above",
    contraindications: ["Pregnancy", "Immunosuppression", "Severe egg allergy"],
    storageConditions: "2-8°C, use within 8 hours once reconstituted",
    nhsEligibility: "NHS childhood programme",
    privatePrice: "£35-45 per dose",
  },
  
  // HPV
  {
    id: "696d189477123322531e344w",
    disease: "HPV (Human Papillomavirus)",
    brandName: "Gardasil 9",
    manufacturer: "Merck",
    displayText: "HPV - Gardasil 9 (Merck)",
    searchTerms: ["hpv", "human papillomavirus", "gardasil", "merck", "cervical cancer"],
    description: "9-valent vaccine against HPV types 6, 11, 16, 18, 31, 33, 45, 52, 58.",
    dosage: "0.5ml",
    schedule: "2 or 3 doses depending on age at first dose",
    route: "Intramuscular",
    ageRange: "9 years and above",
    contraindications: ["Hypersensitivity", "Pregnancy"],
    sideEffects: ["Injection site reactions", "Headache", "Dizziness"],
    storageConditions: "2-8°C",
    nhsEligibility: "Girls & boys 12-13 years (NHS programme)",
    privatePrice: "£165-185 per dose",
  },
  
  // Tetanus/Diphtheria/Polio combinations
  {
    id: "696d189477123322531e344x",
    disease: "Tetanus, Diphtheria & Polio",
    brandName: "Revaxis",
    manufacturer: "Sanofi Pasteur",
    displayText: "Tetanus, Diphtheria & Polio - Revaxis (Sanofi Pasteur)",
    searchTerms: ["tetanus", "diphtheria", "polio", "revaxis", "sanofi", "td", "tdp", "booster"],
    description: "Combined vaccine for tetanus, low-dose diphtheria and polio booster.",
    dosage: "0.5ml",
    schedule: "Single dose (10-yearly booster)",
    route: "Intramuscular",
    ageRange: "6 years and above",
    contraindications: ["Hypersensitivity", "Defer if acute illness"],
    storageConditions: "2-8°C",
    boosterRequired: true,
    boosterSchedule: "Every 10 years",
    nhsEligibility: "NHS for certain groups, travel",
    privatePrice: "£35-45",
  },
  {
    id: "696d189477123322531e345y",
    disease: "Tetanus, Diphtheria & Pertussis",
    brandName: "Boostrix-IPV",
    manufacturer: "GSK",
    displayText: "Tetanus, Diphtheria, Pertussis & Polio - Boostrix-IPV (GSK)",
    searchTerms: ["tetanus", "diphtheria", "pertussis", "polio", "boostrix", "gsk", "whooping cough", "tdap"],
    description: "Combined Tdap-IPV booster vaccine.",
    dosage: "0.5ml",
    schedule: "Single dose",
    route: "Intramuscular",
    ageRange: "4 years and above",
    nhsEligibility: "Pregnant women (whooping cough programme), travel",
    privatePrice: "£40-50",
  },
  
  // Shingles
  {
    id: "696d189477123322531e345z",
    disease: "Shingles (Herpes Zoster)",
    brandName: "Shingrix",
    manufacturer: "GSK",
    displayText: "Shingles - Shingrix (GSK)",
    searchTerms: ["shingles", "shingrix", "gsk", "herpes zoster", "zoster"],
    description: "Recombinant zoster vaccine for prevention of shingles.",
    dosage: "0.5ml",
    schedule: "2 doses, 2-6 months apart",
    route: "Intramuscular",
    ageRange: "50 years and above",
    contraindications: ["Hypersensitivity", "Acute illness"],
    sideEffects: ["Injection site pain", "Myalgia", "Fatigue", "Headache"],
    storageConditions: "2-8°C",
    nhsEligibility: "70-79 years (NHS programme transitioning to Shingrix)",
    privatePrice: "£195-220 per dose",
  },
  
  // Varicella (Chickenpox)
  {
    id: "196d189477123322531e3452",
    disease: "Varicella (Chickenpox)",
    brandName: "Varivax",
    manufacturer: "Merck",
    displayText: "Varicella - Varivax (Merck)",
    searchTerms: ["varicella", "chickenpox", "varivax", "merck"],
    description: "Live attenuated varicella vaccine.",
    dosage: "0.5ml",
    schedule: "2 doses, 4-8 weeks apart",
    route: "Subcutaneous",
    ageRange: "12 months and above",
    contraindications: ["Pregnancy", "Immunosuppression", "Salicylate therapy"],
    storageConditions: "Freezer (-50 to -15°C), or 2-8°C for up to 72 hours",
    nhsEligibility: "Healthcare workers (non-immune), certain at-risk groups",
    privatePrice: "£65-75 per dose",
  },
];

/**
 * Search vaccines by disease name or brand name
 */
export function searchVaccines(query: string): VaccineOption[] {
  if (!query || query.trim() === "") {
    return vaccineDatabase;
  }
  
  const searchQuery = query.toLowerCase().trim();
  
  return vaccineDatabase.filter((vaccine) =>
    vaccine.searchTerms.some((term) => term.includes(searchQuery)) ||
    vaccine.disease.toLowerCase().includes(searchQuery) ||
    vaccine.brandName.toLowerCase().includes(searchQuery) ||
    vaccine.manufacturer.toLowerCase().includes(searchQuery)
  );
}

/**
 * Get vaccine by ID
 */
export function getVaccineById(id: string): VaccineOption | undefined {
  return vaccineDatabase.find((vaccine) => vaccine.id === id);
}
